import {Component, Inject, OnInit, Optional} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {IWorkingPeriodDropdowns} from 'src/app/interfaces/employee-edit-mode.interface';
import {EmployeeStatuses} from 'src/app/interfaces/roles.interace';
import {
  IDecreasePeriodDropdowns,
  IIncreasePeriodDropdowns,
  WorkingPeriodFormGroup,
  workingPeriodStatus,
  workingPeriodStatuses,
} from 'src/app/interfaces/termination.interface';
import {DIALOG_DATA} from 'src/app/services/dialog.service';
import {WorkingPeriodService} from 'src/app/services/working-period/working-period.service';
import {DialogRef} from 'src/app/shared/ui-kits/dialog/dialog-ref';
import {Utils} from 'src/app/shared/utils/utils';

@UntilDestroy()
@Component({
  selector: 'app-working-period-popup',
  templateUrl: './working-period-popup.component.html',
  styleUrls: ['./working-period-popup.component.scss'],
})
export class WorkingPeriodPopupComponent implements OnInit {
  public workingPeriodForm!: FormGroup;
  public statusDropdown!: IDropdownObject[];
  public allDropdown!: IWorkingPeriodDropdowns | null;
  public isAccountBlockDate: boolean = false;
  public validateAllFormFields = Utils.validateAllFormFields;

  constructor(
    private dialogRef: DialogRef,
    @Optional()
    @Inject(DIALOG_DATA)
    public data: {
      employmentStatus: IDropdownObject;
      pmOptionsList: IDropdownObject[];
      lmOptionsList: IDropdownObject[];
      createdBy: IDropdownObject[];
      isPMAllowToChoose: boolean;
    },
    protected workingPeriod: WorkingPeriodService
  ) {}

  ngOnInit(): void {
    this.workingPeriod
      .getDropdowns()
      .pipe(untilDestroyed(this))
      .subscribe(res => this.setDropdownValues(res));

    this.workingPeriodForm = this.buildWorkingPeriodForm();
    this.setWorkingPeriodServiceValue();
  }

  setWorkingPeriodServiceValue() {
    this.workingPeriod.setWorkingPeriod = this.workingPeriodForm;
    this.workingPeriod.setCurrentStatus =
      this.data.employmentStatus?.name ?? EmployeeStatuses.Former;
  }

  setDropdownValues(dropdown: IWorkingPeriodDropdowns) {
    // Filter out the appropriate employment statuses
    dropdown.NewStatus = dropdown.NewStatus.filter(
      (status: IDropdownObject) =>
        status.name !== workingPeriodStatus.Consultant
    ).filter((status: IDropdownObject) => {
      if (this.workingPeriod.getCurrentStatus === workingPeriodStatus.Former) {
        return status.name !== workingPeriodStatus.Former;
      } else if (
        this.workingPeriod.getCurrentStatus === workingPeriodStatus.Employee
      ) {
        return status.name !== workingPeriodStatus.Employee;
      } else if (
        this.workingPeriod.getCurrentStatus === workingPeriodStatus.Consultant
      ) {
        return status.name !== workingPeriodStatus.ContractorPT;
      } else {
        return status.name !== workingPeriodStatus.ContractorPT;
      }
    });

    // Assign filtered dropdowns to the respective class properties
    this.statusDropdown = dropdown.NewStatus;
    this.allDropdown = dropdown;
  }

  save() {
    if (this.workingPeriodForm.invalid) {
      this.validateAllFormFields(this.workingPeriodForm);
      return;
    }

    if (this.workingPeriod.newStatus === workingPeriodStatus.ContractorPT) {
      this.workingPeriod.formControl('stavka')?.setValue(null);
    }

    this.dialogRef.close(this.workingPeriodForm.getRawValue());
  }

  cancel() {
    this.dialogRef.close();
  }

  buildWorkingPeriodForm(): FormGroup<WorkingPeriodFormGroup> {
    return new FormGroup<WorkingPeriodFormGroup>({
      employeeId: new FormControl<number | null>(null),
      newStatus: new FormControl<IDropdownObject | null>(null, [
        Validators.required,
      ]),
      createdBy: new FormControl<IDropdownObject | null>(
        null,
        Validators.required
      ),
      corporateAccount: new FormControl<IDropdownObject | null>(
        null,
        Validators.required
      ),
      equipment: new FormControl<IDropdownObject | null>(
        null,
        Validators.required
      ),
      location: new FormControl<string | null>(null, Validators.required),
      comment: new FormControl<string | null>(null),
    });
  }

  get currentStatusId() {
    const status = Object.keys(workingPeriodStatus).find(
      key =>
        workingPeriodStatus[key as keyof typeof workingPeriodStatus] ===
        this.currentStatus.name
    );
    return workingPeriodStatuses[status as keyof typeof workingPeriodStatuses];
  }

  get currentStatus(): IDropdownObject {
    return this.data.employmentStatus;
  }

  get isFormer(): boolean {
    return this.workingPeriod.getCurrentStatus === workingPeriodStatus.Former;
  }

  get accountCorrespondenceOptionsList(): IDropdownObject[] {
    return this.allDropdown?.AccountCorrespondenceOption ?? [];
  }

  get createdByOptionsList(): IDropdownObject[] {
    return this.data.createdBy;
  }

  get increaseDropdowns(): IIncreasePeriodDropdowns {
    return {
      corporateAccountOptionsList: this.allDropdown?.AccountCreateOption || [],
      equipmentOptionsList: this.allDropdown?.EquipmentProvisionOption || [],
      pmOptionsList: this.data.pmOptionsList || [],
      lmOptionsList: this.data.lmOptionsList || [],
    };
  }

  get decreaseDropdowns(): IDecreasePeriodDropdowns {
    return {
      corporateAccountOptionsList: this.allDropdown?.AccountBlockOption || [],
      equipmentOptionsList: this.allDropdown?.EquipmentOption || [],
      terminationReasonOptionsList: this.allDropdown?.TerminationReason || [],
      accountCorrespondenceOptionsList:
        this.accountCorrespondenceOptionsList || [],
    };
  }

  setFormControlValue(controlName: string, value: any) {
    this.workingPeriod.formControl(controlName)?.setValue(value);
  }
}
